import React, { useState, useEffect } from 'react';
import { authenticatePasskey, getAllPaymentCards, getDefaultCard, getOrder, validateOtp } from '../utils/apiService';
import { getCardType } from '../utils/cardUtils';
import CardManagement from './CardManagement';
import CardItem from "./CardItem";
import AuthenticatePasskeyScreen from './AuthenticatePasskeyScreen';
import VerifyOtpScreen from './VerifyOtpScreen';
import cardImg from "../images/mastercard.png"; // Adjust path as needed
import cardImgVisa from "../images/visa.png"; // Adjust path as needed

function PaymentModal({ userId, sessionId, paymentData, onClose, onConfirm, cardInfo, userInfo, updateUserInfo, countdownMinutes = 30, }) {
  const [password, setPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cards, setCards] = useState([]);
  const [card, setDefaultCard] = useState(cardInfo);
  const [countdown, setCountdown] = useState(countdownMinutes * 60);
  const [orderData, setOrderData] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [showPasskeyScreen, setShowPasskeyScreen] = useState(false);
  const [isVerifyingPasskey, setIsVerifyingPasskey] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [showPasskeyFailedPopup, setShowPasskeyFailedPopup] = useState(false);
  const [passkeyFailMessage, setPasskeyFailMessage] = useState('');

  useEffect(() => {
    const fetchCards = async () => {
      if (userId) {
        try {
          // Fetch all cards first and wait for response
          const allCards = await getAllPaymentCards(userId);
          setCards(allCards);

          

          // Fetch default card
          const response = await getDefaultCard(userId);
          console.log('Default card response:', response);

          if (response.default_card_id) {
            const defaultCard = allCards.find(card => card.card_id === response.default_card_id);

            if (defaultCard) {

              setDefaultCard(defaultCard);
            }
          }
        } catch (error) {
          console.error('Failed to fetch cards:', error);
          setCards([]);
        }
      }
    };

    /*const fetchOrder = async () => {
      const orderId = paymentData?.order_id || paymentData?.orderId || paymentData?.order;
      if (sessionId) {
        setOrderLoading(true);
        try {
          const order = await getOrder(sessionId);
          console.log('Order data fetched:', order);
          setOrderData(order);
        } catch (error) {
          console.error('Failed to fetch order:', error);
        } finally {
          setOrderLoading(false);
        }
      }
    };*/

    fetchCards();
    //fetchOrder();

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [userId, paymentData]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const formatCardNumber = (cardNumber) => {
    let last4 = '';

    if (typeof cardNumber === 'string') {
      const cleaned = cardNumber.replace(/[\s-]/g, '');
      last4 = cleaned.length >= 4 ? cleaned.slice(-4) : cleaned;
    } else if (typeof cardNumber === 'number') {
      last4 = cardNumber.toString().slice(-4);
    } else {
      last4 = '1234';
    }

    return (
      <>
        <span>****</span>
        <span>****</span>
        <span>****</span>
        <span>{last4}</span>
      </>
    );
  };

  const cardType = getCardType(card?.cardNumber);

  const handleConfirm = async () => {
    const isCashBack = cardType === 'mastercard';
    setIsProcessing(true);
    
    // Show passkey authentication screen
    setShowPasskeyScreen(true);
    setIsProcessing(false);
  };

  const handleVerifyPasskey = async () => {
    const isCashBack = cardType === 'mastercard';
    setIsVerifyingPasskey(true);

    try {
      await authenticatePasskey(userId, sessionId);
      setShowPasskeyScreen(false);
      setIsProcessing(true);
      const success = await onConfirm(password, isCashBack);
      if (!success) {
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Passkey authentication failed:', error);
      setShowPasskeyScreen(false);
      setPasskeyFailMessage(error.message || 'Passkey authentication could not be completed.');
      setShowPasskeyFailedPopup(true);
    } finally {
      setIsVerifyingPasskey(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue.trim()) {
      setOtpError('Please enter OTP');
      return;
    }
    const isCashBack = cardType === 'mastercard';
    setIsVerifyingOtp(true);
    setOtpError('');
    try {
      await validateOtp(userId, sessionId, otpValue);
      setShowOtpScreen(false);
      setIsProcessing(true);
      const success = await onConfirm(password, isCashBack);
      if (!success) {
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('OTP verification failed:', error);
      setOtpError(error.message || 'OTP verification failed. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCancelOtp = () => {
    setShowOtpScreen(false);
    setOtpValue('');
    setOtpError('');
  };

  const handleCancelPasskey = () => {
    setShowPasskeyScreen(false);
  };

  const handlePasskeyFailedContinueToOtp = () => {
    setShowPasskeyFailedPopup(false);
    setPasskeyFailMessage('');
    setOtpValue('');
    setOtpError('');
    setShowOtpScreen(true);
  };

  const handlePasskeyFailedClose = () => {
    setShowPasskeyFailedPopup(false);
    setPasskeyFailMessage('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Debug: Log paymentData to see what we're receiving
  useEffect(() => {
    console.log('PaymentModal - paymentData received:', paymentData);
    console.log('PaymentModal - order_id:', paymentData?.order_id);
    console.log('PaymentModal - orderId:', paymentData?.orderId);
    console.log('PaymentModal - order:', paymentData?.order);
  }, [paymentData]);

  const orderId = paymentData?.order_id || paymentData?.orderId || paymentData?.order || '-';


  const handleCardSelected = (selectedCard) => {

    // Update user info with selected card
    const updatedUserInfo = {
      ...userInfo,
      cardInfo: {
        cardNumber: selectedCard.cardNumber,
        cardHolder: selectedCard.cardHolder,
        expiryDate: selectedCard.expiryDate,
        cvv: selectedCard.cvv,
        isMastercard: getCardType(selectedCard.cardNumber) === 'mastercard'

      }
    };

    console.log('Updated user info with selected card:', updatedUserInfo.cardInfo?.isMastercard);
    updateUserInfo(updatedUserInfo);
    setDefaultCard(selectedCard);


  };

  // Format countdown display
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="payment-modal open" onClick={handleBackdropClick}>

      <div className="payment-modal-content">

        {/* Styled like PaymentCard */}
        <div className="payment-card-inline">
          {/* Secure Payment Header */}
          <div className="payment-header">
            <div className="secure-badge">
              <span className="secure-icon">🟢</span>
              <span className="secure-text">Secure Payment</span>
            </div>
            <div className="countdown-timer">
              Complete payment within {formatCountdown(countdown)}
            </div>
          </div>

          {/* Price Display */}
          <div className="payment-price-section">
            <div className="total-price">
              {paymentData?.order_currency || orderData?.currency || 'SGD'}{' '}
              {typeof paymentData?.order_price === 'number'
                ? paymentData.order_price.toLocaleString('en-US', { minimumFractionDigits: 2 })
                : orderData?.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || paymentData?.order_price}
            </div>

            {/* Order Items from API */}
            {orderData && orderData.items && orderData.items.length > 0 && (
              <div className="payment-order-items">
                <div className="order-items-title">Order Details:</div>
                {orderData.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="order-item-label">{item.label}</div>
                    <div className="order-item-amount">
                      {item.amount.currency} {item.amount.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {orderLoading && (
              <div className="order-loading">Loading order details...</div>
            )}

            {/* Booking Summary */}
            {paymentData?.booking_type === 'flight' && (
              <div className="payment-booking-summary">
                {paymentData.outbound_id && (
                  <div className="booking-route">
                    Outbound: {paymentData.outbound_id}
                  </div>
                )}
                {paymentData.return_id && (
                  <div className="booking-route">
                    Return: {paymentData.return_id}
                  </div>
                )}
              </div>
            )}
            {paymentData?.booking_type === 'hotel' && (
              <div className="payment-booking-summary">
                {paymentData.hotel_name && (
                  <div className="booking-hotel-name">{paymentData.hotel_name}</div>
                )}
                {(paymentData.check_in || paymentData.check_out) && (
                  <div className="booking-dates">
                    {paymentData.check_in} - {paymentData.check_out}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Card Selection */}
          <div className="payment-cards-section">
            {cards.length === 0 ? (
              <div className="cards-loading">Loading payment methods...</div>
            ) : (
              <>
                {cards.map(cardItem => {
                  const benefits = cardType === 'mastercard' && cardItem.card_id === card?.card_id
                    ? { text: '5% cash rebate', type: 'cashback' }
                    : null;
                  return (
                    <label
                      key={cardItem.card_id}
                      className={`card-option ${card?.card_id === cardItem.card_id ? 'selected' : ''}`}
                      onClick={() => handleCardSelected(cardItem)}
                    >
                      <input
                        type="radio"
                        name="payment-card"
                        value={cardItem.card_id}
                        checked={card?.card_id === cardItem.card_id}
                        readOnly
                      />
                      <span className="card-icon">
                        {getCardType(cardItem.cardNumber) === 'visa' ? '💳' : '💳'}
                      </span>
                      <span className="card-name">
                        {cardItem.cardName || 'POSB Everyday Card'} <p>{formatCardNumber(cardItem.cardNumber)}</p>
                      </span>
                      {benefits && (
                        <span className={`card-benefit ${benefits.type}`}>
                          {benefits.text}
                        </span>
                      )}
                    </label>
                  );
                })}

               

                {/* Apple Pay option */}
                <label className="card-option apple-pay">
                  <input
                    type="radio"
                    name="payment-card"
                    value="apple-pay"
                    checked={false}

                  />
                  <span className="card-name">Apple Pay</span>
                </label>
              </>
            )}
          </div>

          {/* Pay Now Button */}
          <div className="payment-actions">
            <button
              className="pay-now-btn"
              onClick={handleConfirm}
              disabled={isProcessing || !card}
            >
              {isProcessing ? 'Processing...' : 'Pay now'}
            </button>
          </div>
        </div>
      </div>

      <AuthenticatePasskeyScreen
        isVisible={showPasskeyScreen}
        isVerifying={isVerifyingPasskey}
        onVerify={handleVerifyPasskey}
        onCancel={handleCancelPasskey}
      />

      {showPasskeyFailedPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001,
            backdropFilter: 'blur(4px)'
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="passkey-failed-title"
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '28px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
            }}
          >
            <h3
              id="passkey-failed-title"
              style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600', color: '#333' }}
            >
              Passkey authentication failed
            </h3>
            <p style={{ margin: '0 0 24px 0', color: '#555', fontSize: '15px', lineHeight: 1.5 }}>
              {passkeyFailMessage}
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px', lineHeight: 1.5 }}>
              You can verify your payment with a one-time password (OTP) sent to your device instead.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={handlePasskeyFailedContinueToOtp}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Continue with OTP
              </button>
              <button
                type="button"
                onClick={handlePasskeyFailedClose}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <VerifyOtpScreen
        isVisible={showOtpScreen}
        otpValue={otpValue}
        otpError={otpError}
        isVerifying={isVerifyingOtp}
        onOtpChange={(e) => {
          setOtpValue(e.target.value);
          setOtpError('');
        }}
        onVerify={handleVerifyOtp}
        onCancel={handleCancelOtp}
      />
    </div>
  );
}

export default PaymentModal;
